import { useCallback, useEffect, useRef, useState } from "react";

import { clamp } from "@perceptron/utils/math";
import Konva from "konva";
import type { KonvaEventObject } from "konva/lib/Node";

import { SCROLL_SPEED_PX_PER_SECOND } from "../constants";
import { useTrackedRect } from "./useTrackedRect";

/** A dropped or backgrounded frame must not jump the timeline. */
const AUTO_SCROLL_MAX_STEP_SECONDS = 0.1;

type UseScrubbingArgs = {
  pixelsPerSecond: number;
  scrollLeft: number;
  durationSeconds: number;
  width: number;
  maxScroll: number;
  setScrollLeft?: (fn: (prev: number) => number) => void;
  onScrub?: (t: number | null) => void;
};

type UseScrubbingResult = {
  /** Attach to the `<Stage>` so we can read its bounding rect for scrub coords. */
  stageRef: React.RefObject<Konva.Stage | null>;
  /** Spread onto the `<Stage>`'s `onPointerDown` to start a scrub gesture. */
  onPointerDown: (e: KonvaEventObject<PointerEvent>) => void;
};

/**
 * Encapsulates the scrub gesture: pointer-down on a Konva Stage starts a
 * scrub, window-level pointermove/up listeners drive the gesture, and an
 * auto-scroll loop runs when the pointer leaves the Stage horizontally.
 *
 * Used by the three timeline canvases (ruler, scrub-bar, tracks). The Stage
 * itself is owned by each caller so they can render their own Layer content
 * without a single component juggling all three roles.
 */
export const useScrubbing = ({
  pixelsPerSecond,
  scrollLeft,
  durationSeconds,
  width,
  maxScroll,
  setScrollLeft,
  onScrub,
}: UseScrubbingArgs): UseScrubbingResult => {
  const stageRef = useRef<Konva.Stage>(null);
  const scrollLeftRef = useRef(scrollLeft);
  const currentPointerXRef = useRef<number>(0);
  const rafIdRef = useRef<number | null>(null);
  const pendingMouseEventRef = useRef<{ clientX: number } | null>(null);
  const scrubbingRef = useRef(false);

  const getStageContainer = useCallback(() => stageRef.current?.container() ?? null, []);
  const stageRectRef = useTrackedRect(getStageContainer);

  // Keep the latest onScrub callback in a ref so the window listeners (which
  // are attached once per scrub gesture) always invoke the current closure.
  const onScrubRef = useRef(onScrub);
  useEffect(() => {
    onScrubRef.current = onScrub;
  }, [onScrub]);

  const [isScrubbing, setIsScrubbing] = useState(false);
  const [scrollVelocity, setScrollVelocity] = useState(0);

  useEffect(() => {
    scrollLeftRef.current = scrollLeft;
  }, [scrollLeft]);

  const handleScrub = useCallback(
    (pointerX: number) => {
      const newTime = (pointerX + scrollLeftRef.current) / pixelsPerSecond;
      const clampedTime = Math.max(0, Math.min(newTime, durationSeconds));
      onScrubRef.current?.(clampedTime);
    },
    [pixelsPerSecond, durationSeconds],
  );

  useEffect(() => {
    if (!isScrubbing) {
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      return;
    }

    const stage = stageRef.current;
    if (stage) {
      stageRectRef.current = stage.container().getBoundingClientRect();
    }

    const processScrubEvent = () => {
      const event = pendingMouseEventRef.current;
      if (!event || !stageRectRef.current) {
        rafIdRef.current = null;
        return;
      }

      const stageRect = stageRectRef.current;
      const pointerX = event.clientX - stageRect.left;
      currentPointerXRef.current = pointerX;

      const isInside = event.clientX >= stageRect.left && event.clientX <= stageRect.right;
      if (isInside) {
        setScrollVelocity(0);
      } else {
        setScrollVelocity(event.clientX < stageRect.left ? -SCROLL_SPEED_PX_PER_SECOND : SCROLL_SPEED_PX_PER_SECOND);
      }
      handleScrub(pointerX);

      pendingMouseEventRef.current = null;
      rafIdRef.current = null;
    };

    const handleWindowMove = (e: PointerEvent) => {
      if (!scrubbingRef.current) return;
      pendingMouseEventRef.current = { clientX: e.clientX };
      if (rafIdRef.current === null) {
        rafIdRef.current = requestAnimationFrame(processScrubEvent);
      }
    };

    const endScrub = () => {
      scrubbingRef.current = false;
      pendingMouseEventRef.current = null;
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      setIsScrubbing(false);
      setScrollVelocity(0);
      onScrubRef.current?.(null);
    };

    const handleWindowUp = (event: PointerEvent) => {
      if (!scrubbingRef.current) return;
      // Pointerup can arrive before the queued move's animation frame. Its
      // position is authoritative; apply it before ending the scrub gesture.
      if (stageRectRef.current) handleScrub(event.clientX - stageRectRef.current.left);
      endScrub();
    };

    const handleWindowCancel = () => {
      if (scrubbingRef.current) endScrub();
    };

    window.addEventListener("pointermove", handleWindowMove);
    window.addEventListener("pointerup", handleWindowUp);
    window.addEventListener("pointercancel", handleWindowCancel);
    return () => {
      window.removeEventListener("pointermove", handleWindowMove);
      window.removeEventListener("pointerup", handleWindowUp);
      window.removeEventListener("pointercancel", handleWindowCancel);
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      pendingMouseEventRef.current = null;
    };
  }, [isScrubbing, handleScrub, stageRectRef]);

  // Auto-scroll while the pointer is held outside the Stage horizontally. The
  // speed is per second of elapsed time, so 60 Hz and 120 Hz displays match.
  useEffect(() => {
    if (!isScrubbing || scrollVelocity === 0) return;
    let animationFrameId: number;
    let lastFrameTime: number | null = null;
    let lastScrubTime: number | null = null;

    const autoScrollLoop = (frameTime: number) => {
      if (!scrubbingRef.current) return;
      const elapsedSeconds =
        lastFrameTime === null
          ? 1 / 60
          : Math.min(AUTO_SCROLL_MAX_STEP_SECONDS, Math.max(0, (frameTime - lastFrameTime) / 1000));
      lastFrameTime = frameTime;
      const newScroll = clamp(scrollLeftRef.current + scrollVelocity * elapsedSeconds, 0, maxScroll);
      if (newScroll !== scrollLeftRef.current) {
        scrollLeftRef.current = newScroll;
        setScrollLeft?.(() => newScroll);
      }

      const fallbackPointerX = scrollVelocity > 0 ? width : 0;
      const pointerX = currentPointerXRef.current || fallbackPointerX;
      const newTime = (pointerX + newScroll) / pixelsPerSecond;
      const clampedTime = Math.max(0, Math.min(newTime, durationSeconds));
      // Parked at either end, the time stops changing: don't keep re-seeking it.
      if (clampedTime !== lastScrubTime) {
        lastScrubTime = clampedTime;
        onScrubRef.current?.(clampedTime);
      }

      animationFrameId = requestAnimationFrame(autoScrollLoop);
    };

    animationFrameId = requestAnimationFrame(autoScrollLoop);
    return () => cancelAnimationFrame(animationFrameId);
  }, [isScrubbing, scrollVelocity, maxScroll, width, pixelsPerSecond, durationSeconds, setScrollLeft]);

  const onPointerDown = useCallback(
    (e: KonvaEventObject<PointerEvent>) => {
      // Only start scrubbing on background hits — not on interactive shapes
      // like segments, which have their own click handlers.
      const target = e.target;
      const targetName = target.getClassName();
      const shapeName = target.name();
      const isSegment = shapeName === "segment";
      const isBackground = !isSegment && (targetName === "Layer" || targetName === "Rect" || targetName === "Stage");
      if (!isBackground) return;

      const pointer = e.target.getStage()?.getPointerPosition();
      if (!pointer) return;
      scrubbingRef.current = true;
      currentPointerXRef.current = pointer.x;
      handleScrub(pointer.x);
      setIsScrubbing(true);
    },
    [handleScrub],
  );

  return { stageRef, onPointerDown };
};
